import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useTranslation } from '../../hooks/useTranslation';
import { profileAPI } from '../../services/api';
import { Upload, X, User } from 'lucide-react';
import { useToast } from '../ui/use-toast';

const PersonalInfoTab = ({ profile, onUpdate }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    first_name: profile?.personal?.first_name || '',
    last_name: profile?.personal?.last_name || '',
    email: profile?.personal?.email || '',
    phone: profile?.personal?.phone || '',
    language: profile?.personal?.language || 'en',
    timezone: profile?.personal?.timezone || 'UTC',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(profile?.personal?.avatar_url || null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;

    setUploading(true);
    try {
      await profileAPI.uploadAvatar(avatarFile);
      toast({
        title: t('common.success'),
        description: 'Avatar uploaded successfully',
      });
      onUpdate();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || 'Failed to upload avatar',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await profileAPI.updatePersonal(formData);
      toast({
        title: t('common.success'),
        description: 'Personal information updated successfully',
      });
      onUpdate();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || 'Failed to update personal information',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{t('profile.personal.title')}</h3>
        <p className="text-sm text-gray-500 mt-1">{t('profile.personal.subtitle')}</p>
      </div>

      {/* Avatar Upload */}
      <div className="flex items-center space-x-6">
        <div className="relative">
          {avatarPreview ? (
            <img
              src={avatarPreview}
              alt="Avatar"
              className="h-24 w-24 rounded-full object-cover border-4 border-gray-200"
            />
          ) : (
            <div className="h-24 w-24 rounded-full bg-gradient-premium flex items-center justify-center">
              <User className="h-12 w-12 text-white" />
            </div>
          )}
          {avatarPreview && (
            <button
              onClick={() => {
                setAvatarPreview(null);
                setAvatarFile(null);
              }}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex-1">
          <Label htmlFor="avatar" className="cursor-pointer">
            <div className="flex items-center space-x-2 text-sm text-primary-600 hover:text-primary-700">
              <Upload className="h-4 w-4" />
              <span>{t('profile.personal.uploadAvatar')}</span>
            </div>
            <Input
              id="avatar"
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </Label>
          {avatarFile && (
            <Button
              onClick={handleAvatarUpload}
              disabled={uploading}
              size="sm"
              className="mt-2"
            >
              {uploading ? t('common.loading') : t('common.upload')}
            </Button>
          )}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name">{t('profile.personal.firstName')}</Label>
            <Input
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className="input-premium mt-1"
            />
          </div>
          <div>
            <Label htmlFor="last_name">{t('profile.personal.lastName')}</Label>
            <Input
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className="input-premium mt-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email">{t('profile.personal.email')}</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className="input-premium mt-1"
            disabled
          />
        </div>

        <div>
          <Label htmlFor="phone">{t('profile.personal.phone')}</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            className="input-premium mt-1"
            placeholder="+33 6 12 34 56 78"
          />
        </div>

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={saving} className="btn-premium">
            {saving ? t('common.loading') : t('profile.personal.saveButton')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PersonalInfoTab;
